import { AppError } from '../errors/app-error.js';
import { decisionGrants } from './network-submission.entity.js';
/** Id determinista. El doble guión evita que un `programId` con guión simple
 *  produzca el mismo id que otra combinación — improbable, pero el costo de
 *  evitarlo es un carácter. */
export function buildCertificationId(programId, capCode, level) {
    return `${programId}__${capCode}__${level}`;
}
/** Una certificación se otorga SOLO desde una ronda aprobada cuyo asunto es una
 *  propuesta CAP. La guarda existe porque el callable que decide es UNO SOLO
 *  para los dos asuntos: sin esto, aprobar una ronda de membresía podría
 *  certificar un programa si alguien encadena mal los parámetros. */
export function assertGrantable(input) {
    if (input.subject !== 'capProposal') {
        throw AppError.validation(`Only a CAP proposal round grants a certification (subject: ${input.subject})`, 'cap.certification_wrong_subject');
    }
    // Aprobar CON CONDICIONES también otorga: el centro queda certificado y puede
    // operar; lo que cambia es que TMAI se comprometió a volver a mirar.
    if (!decisionGrants(input.decision)) {
        throw AppError.validation(`Only an approved round grants a certification (decision: ${input.decision})`, 'cap.certification_requires_approval');
    }
    if (!input.proposalId) {
        throw AppError.validation('The round does not reference a proposal, so nothing can be certified', 'cap.certification_missing_proposal');
    }
}
/** Las condiciones tienen que llegar CON la decisión que las crea, y sólo con
 *  ella.
 *
 *  Las dos direcciones importan. Otorgar «con condiciones» sin nombrarlas
 *  simula rigor: el expediente diría que hubo reservas y no habría forma de
 *  saber cuáles ni hasta cuándo. Y mandar condiciones en una aprobación limpia
 *  dejaría una certificación con deudas que nadie declaró — la pantalla diría
 *  «aprobada» y el dato diría otra cosa. */
export function assertConditions(decision, conditions, now) {
    if (decision !== 'approved_with_conditions') {
        if (conditions) {
            throw AppError.validation(`Only a conditional approval carries conditions (decision: ${decision})`, 'cap.conditions_not_applicable');
        }
        return;
    }
    if (!conditions?.items?.length) {
        throw AppError.validation('A conditional approval must state at least one condition', 'cap.conditions_required');
    }
    if (!conditions.reviewBy) {
        throw AppError.validation('A conditional approval must state when TMAI reviews it again', 'cap.conditions_review_date_required');
    }
    // Una fecha ya pasada no compromete a nada: nacería vencida.
    if (conditions.reviewBy <= now) {
        throw AppError.validation(`The review date must be in the future (got ${conditions.reviewBy})`, 'cap.conditions_review_date_past');
    }
}
/** Revocar es un acto explícito y motivado, nunca un efecto lateral — mismo
 *  criterio que bajar de etapa a un seminario (decisión del director,
 *  2026-08-05). Una certificación ya revocada no se revoca de nuevo: el segundo
 *  acto pisaría la fecha y el motivo del primero, que es el que vale. */
export function assertRevocable(certification, reason) {
    if (certification.status !== 'granted') {
        throw AppError.validation('This certification is not in force, so it cannot be revoked', 'cap.certification_not_in_force');
    }
    if (reason.trim() === '') {
        throw AppError.validation('Revoking a certification requires a reason', 'cap.certification_revoke_requires_reason');
    }
}
//# sourceMappingURL=cap-certification.entity.js.map